import { createSlice } from '@reduxjs/toolkit';
import { Analytics } from 'packages';
import dayjs from 'dayjs';
import { VIEW_ITEM_STATES } from './constants';
import { getItemApi, placeBidApi } from './api';
import logger from 'packages/logger';
import * as analytics from '../../../apps/tradera/components/vip/helpers/analytics';
import itemActions from '../../items/actions';
import { assertNoUndefinedProperties } from 'tradera-utils/assert';
import adsActions from 'tradera-state/ads/actions';
import pageActions from 'tradera-state/page/actions';
import {
    selectBidInfo,
    selectItemId,
    selectItemDetails,
    selectBreadcrumbs,
    selectViewItem
} from './selectors';
import GtmService from 'static/script/app/ui/google-tagmanager-service';
import { getItemState } from './item-state-helper';
import history from 'tradera-utils/history';
import { BID_VALIDATION_RESULTS } from 'tradera-apps/tradera/components/vip/api/vip-api-constants';

const initialPlaceBidResponse = {
    info: null,
    isSuccessful: null,
    validationResult: null,
    error: null
};

export const translatedDescriptionStates = {
    DEFAULT: 'DEFAULT',
    LOADING: 'LOADING',
    SERVER_ERROR: 'SERVER_ERROR',
    TRANSLATION_ON_DEMAND_DISABLED: 'TRANSLATION_ON_DEMAND_DISABLED'
};

export const initialState = {
    initialBidFormValue: null,
    bidInfo: null,
    breadCrumbs: [],
    isBidding: false,
    itemDetails: {},
    itemState: null,
    viewItemState: null,
    myMaxBidAmount: null,
    placeBidResponse: initialPlaceBidResponse,
    serverSecondsLeft: null,
    translatedDescription: null,
    translatedDescriptionState: translatedDescriptionStates.DEFAULT,
    showLiveUpdates: false,
    reduxContainer: null
};

const viewItemSlice = createSlice({
    name: 'viewItem',
    initialState,
    reducers: {
        resetPlaceBidResponse: state => {
            state.placeBidResponse = initialPlaceBidResponse;
        },
        resetInitialState: () => {
            return initialState;
        },
        setInitialBidFormValue: (state, { payload }) => {
            state.initialBidFormValue = payload;
        },
        setIsApprovedBuyer: (state, { payload }) => {
            state.itemDetails.isApprovedBuyer = payload;
        },
        setIsInternationalWacko: (state, { payload }) => {
            state.itemDetails.isWackoViewModel.isInternationalWacko = payload;
        },
        setVipLiveUpdate: (state, { payload: { showLiveUpdates } }) => {
            state.showLiveUpdates = showLiveUpdates;
        },
        setItemDetails: (state, { payload: itemDetails }) => {
            state.itemDetails = mapItemDetails(itemDetails);
        },
        setBreadCrumbs: (state, { payload: breadCrumbs }) => {
            state.breadCrumbs = breadCrumbs.map(({ href, text }, index) => ({
                id: index,
                url: href,
                name: text
            }));
        },
        setIsBidding: (state, { payload: isBidding }) => {
            state.isBidding = isBidding;
        },
        setPlaceBidResponse: (
            state,
            { payload: { isSuccessful, validationResult, bidError } }
        ) => {
            state.placeBidResponse.isSuccessful = isSuccessful;
            state.placeBidResponse.validationResult = validationResult;
            state.placeBidResponse.error = bidError;
        },
        setBidInfo: (state, { payload: { bidInfo, itemId, memberId } }) => {
            // Ensure bid is for current item.
            if (state.itemDetails?.itemId !== itemId) {
                logger(new Error(`setBidInfo was not for current item`), {
                    contexts: {
                        items: {
                            currentItemId: state.itemDetails?.itemId,
                            bidInfoItemId: itemId
                        }
                    }
                });
                return;
            }

            // Ensure incoming data is for latest bid. It is possible to get stale data in
            // the response from place bid due to eventual consistency. When that happens
            // we should have received the latest bid before via web socket. For items where
            // web socket updates are not enabled (too long time left of auction) the effect
            // is considered acceptable. Just make sure to not overwrite newer data here.
            const isFirstBidInfo = !state.bidInfo;
            const isLatestBid =
                isFirstBidInfo || state.bidInfo.bidId <= bidInfo.bidId;
            if (!isLatestBid) return;

            const bidStatus = getLeadingBidStatus(
                bidInfo,
                memberId,
                state.bidInfo
            );

            const existingBidCount = state.bidInfo?.bidCount || 0;
            const incomingBidCount = bidInfo.bidCount || 0;
            const newBidInfo = {
                bidId: bidInfo.bidId || 0,
                leadingBidAmount: bidInfo.leadingBidAmount || 0,
                leadingBidderMemberId: bidInfo.leadingBidderMemberId || null,
                leadingBidMemberAlias: bidInfo.leadingBidMemberAlias || null,
                nextValidBidAmount: bidInfo.nextValidBidAmount,
                transactionType: state.bidInfo?.transactionType
                    ? state.bidInfo.transactionType
                    : bidInfo.transactionType || null,

                // Never update with a lower bidCount. Can happen if place-bid-response comes before WebSocket event.
                bidCount:
                    incomingBidCount > existingBidCount
                        ? incomingBidCount
                        : existingBidCount,

                // Never reset if reservePriceReached was true. Can happen if place-bid-response comes before WebSocket event.
                reservePriceReached:
                    state.bidInfo?.reservePriceReached ||
                    bidInfo.reservePriceReached ||
                    false
            };

            assertNoUndefinedProperties(newBidInfo);

            state.bidInfo = { ...newBidInfo, ...bidStatus };
        },
        setMyMaxBidAmount: (state, { payload }) => {
            state.myMaxBidAmount = payload;
        },
        setItemState: (state, { payload }) => {
            state.itemState = payload;
        },
        setViewItemState: (state, { payload: viewItemState }) => {
            state.viewItemState = viewItemState;
        },
        setServerSecondsLeft: (state, { payload }) => {
            state.serverSecondsLeft = payload;
        },
        setTranslatedDescription: (state, { payload }) => {
            state.translatedDescription = { ...payload };
            state.translatedDescriptionState =
                translatedDescriptionStates.DEFAULT;
        },
        setTranslationOnDemandDisabled: state => {
            state.translatedDescription = null;
            state.translatedDescriptionState =
                translatedDescriptionStates.TRANSLATION_ON_DEMAND_DISABLED;
        },
        resetTranslatedDescription: {
            reducer: state => {
                state.translatedDescription = null;
                state.translatedDescriptionState =
                    translatedDescriptionStates.DEFAULT;
            },
            prepare: payload => {
                Analytics.trackEvent({
                    category: 'View Item Page',
                    action: 'Translation',
                    label: 'Hide'
                });
                return { payload };
            }
        },
        beginLoadingTranslatedDescription: state => {
            state.translatedDescription = null;
            state.translatedDescriptionState =
                translatedDescriptionStates.LOADING;
        },
        failedLoadingTranslatedDescription: state => {
            state.translatedDescriptionState =
                translatedDescriptionStates.SERVER_ERROR;
        },
        setSellerItems: (state, { payload }) => {
            state.sellerItems = payload;
        },
        setReduxContainer: (state, { payload }) => {
            state.reduxContainer = payload;
        }
    }
});

export default viewItemSlice.reducer;

export const mapItemDetails = itemDetails => ({
    ...itemDetails,
    descriptionLanguageCodeIso2: itemDetails.descriptionLanguageCodeIso2.toLowerCase(),
    images: itemDetails.images.map(img => ({
        thumbnail: img.thumbnail,
        multiThumbnail: img.thumbnail,
        medium: img.medium || img.small,
        normal: img.normal,
        smallFit: img.smallFit,
        mediumFit: img.mediumFit,
        largeFit: img.largeFit,
        alt: img.alt || img.altText
    }))
});

export const handleItemModel = (itemModel, memberId) => (
    dispatch,
    getState
) => {
    const previousItemId = selectItemId(getState());
    dispatch(viewItemSlice.actions.setItemDetails(itemModel.itemDetails));

    dispatch(
        itemActions.setItemWishList(
            itemModel.itemDetails.itemId,
            itemModel.itemDetails.inWishList
        )
    );
    dispatch(
        viewItemSlice.actions.setVipLiveUpdate({
            showLiveUpdates: itemModel.showLiveUpdates
        })
    );
    dispatch(viewItemSlice.actions.setBreadCrumbs(itemModel.breadCrumbs));
    dispatch(
        viewItemSlice.actions.setBidInfo({
            bidInfo: itemModel.bidInfo,
            itemId: itemModel.itemDetails.itemId,
            memberId
        })
    );

    if (itemModel.bidInfo.maxBidAmount) {
        dispatch(
            viewItemSlice.actions.setMyMaxBidAmount(
                itemModel.bidInfo.maxBidAmount
            )
        );
    }
    dispatch(
        viewItemSlice.actions.setViewItemState(
            itemModel.itemDetails.hasEnded
                ? VIEW_ITEM_STATES.ENDED
                : VIEW_ITEM_STATES.ACTIVE
        )
    );
    dispatch(
        updateItemState(
            selectItemDetails(getState()),
            selectBidInfo(getState())
        )
    );

    dispatch(viewItemSlice.actions.setReduxContainer(itemModel.reduxContainer));
    dispatch(
        viewItemSlice.actions.setServerSecondsLeft(itemModel.serverSecondsLeft)
    );

    const isNewPage = previousItemId != selectItemId(getState());
    if (isNewPage) {
        dispatch(handleNewPage);
    }
};

export const handleNewPage = (dispatch, getState) => {
    const reduxContainer = selectViewItem(getState()).reduxContainer;

    dispatch(adsActions.setAds(reduxContainer.ads));
    dispatch(pageActions.setPageMeta(reduxContainer.pageMeta));

    const currentItemDetails = selectItemDetails(getState());
    const currentBidInfo = selectBidInfo(getState());
    const breadCrumbs = selectBreadcrumbs(getState());
    const swedishCategoryPath = breadCrumbs.map(b => b.name).join(' > ');

    const getAuctionState = () => {
        const hasWon =
            currentItemDetails.hasEnded && currentBidInfo.hasLeadingBid;
        const isLeading =
            !currentItemDetails.hasEnded && currentBidInfo.hasLeadingBid;
        if (hasWon) return 'won';
        if (isLeading) return 'leading';
        return currentItemDetails.hasEnded ? 'ended' : 'normal';
    };

    const contentGroup = `${currentItemDetails.itemType}-${getAuctionState()}`;
    GtmService.trackGtmEvent('viewItem', {
        page: {
            itemState: contentGroup,
            'content-group-2': swedishCategoryPath,
            'content-group-3': contentGroup
        },
        itemType: currentItemDetails.itemType,
        itemId: currentItemDetails.itemId,
        itemFixedPrice: currentItemDetails.fixedPrice || 0,
        itemCategory: swedishCategoryPath,
        itemStatus: currentItemDetails.hasEnded ? 'inactive' : 'active',
        itemEndDate: dayjs(currentItemDetails.endDate).format('YYYYMMDD')
    });
};

const updateItemState = (itemDetails, bidInfo) => dispatch => {
    const itemState = getItemState(
        itemDetails.hasEnded,
        itemDetails.itemType,
        bidInfo.bidCount,
        bidInfo.reservePriceReached,
        itemDetails.openingBid,
        bidInfo.nextValidBidAmount,
        bidInfo.transactionType,
        bidInfo.leadingBidderMemberId
    );
    dispatch(viewItemSlice.actions.setItemState(itemState));
};

/**
 * It is possible that we need to update the URL
 * when an auction that expires is automatically
 * restarted and gets a new itemId. Without this,
 * we could end up in an endless recursion of
 * trying to fetch data for the itemId in the URL.
 * @param {*} currentItemId
 * @param {*} itemModel
 */
export const ensureCorrectUrlForItem = (expectedItemId, itemModel) => {
    const incomingItemId = itemModel.itemDetails.itemId;
    if (expectedItemId !== incomingItemId) {
        // Restarted item, update URL
        history.replace({
            pathname: itemModel.reduxContainer.pageMeta.canonicalLink,
            search: window.location.search
        });
    }
};

export const fetchItemAndBidInfo = (
    itemId,
    shouldResetInititalState,
    setLoading,
    setLoaded
) => async (dispatch, getState) => {
    setLoading && setLoading();
    const { memberId = 0 } = getState().member;
    if (shouldResetInititalState) {
        dispatch(viewItemSlice.actions.resetInitialState());
    }
    try {
        const itemModel = (await getItemApi(itemId)).data;
        dispatch(handleItemModel(itemModel, memberId));
        setLoaded && setLoaded();
        ensureCorrectUrlForItem(itemId, itemModel);
    } catch (error) {
        const status = error.response && error.response.status;
        if (status === 404) {
            // Handle server side.
            window.location.reload();
        } else {
            logger(error);
        }
    }
};

const getLeadingBidStatus = (bidInfo, memberId, previousBidInfo = null) => {
    const hadLeadingBid = !!previousBidInfo?.hasLeadingBid;
    const hasLeadingBid =
        !!memberId && memberId === bidInfo.leadingBidderMemberId;
    const hadLostLeadingBid = !!previousBidInfo?.hasLostLeadingBid;
    const hasLostLeadingBid =
        (hadLostLeadingBid || hadLeadingBid) && !hasLeadingBid;
    return {
        // ...bidInfo,
        hasLostLeadingBid,
        hasLeadingBid
    };
};

export const toggleWishList = (itemId, forceAdd = false) => (
    dispatch,
    getState
) => {
    const shouldNotTrackWishlistEventBecauseNeedsToBeSameAsLegacyVip = true;
    const inWishListOldValue = getState().items.wishListItemIds.has(itemId);
    const inWishListNewValue = !inWishListOldValue || forceAdd;

    const itemDetails = getState().views.viewItem.itemDetails;
    const wishListCountOldValue = itemDetails.wishListCount;
    let wishListCountNewValue = wishListCountOldValue;
    if (Number.isInteger(wishListCountNewValue)) {
        if (inWishListOldValue && !inWishListNewValue) {
            wishListCountNewValue--;
        } else if (!inWishListOldValue && inWishListNewValue) {
            wishListCountNewValue++;
        }
    }
    dispatch(
        viewItemSlice.actions.setItemDetails({
            ...itemDetails,
            inWishList: inWishListNewValue,
            wishListCount: wishListCountNewValue
        })
    );

    return dispatch(
        itemActions.wishlist(
            itemId,
            inWishListNewValue,
            shouldNotTrackWishlistEventBecauseNeedsToBeSameAsLegacyVip
        )
    ).then(inWishListNewValue => {
        if (inWishListNewValue !== undefined) {
            analytics.trackWishlist(itemId, inWishListNewValue);
        } else {
            //Restore previous value;
            const itemDetails = getState().views.viewItem.itemDetails;
            dispatch(
                viewItemSlice.actions.setItemDetails({
                    ...itemDetails,
                    inWishList: inWishListOldValue,
                    wishListCount: wishListCountOldValue
                })
            );
        }
        return inWishListNewValue;
    });
};

export const placeBid = (itemId, memberId, amount, onLoggedOut) => async (
    dispatch,
    getState
) => {
    dispatch(viewItemSlice.actions.setIsBidding(true));
    try {
        const result = await placeBidApi(itemId, amount, onLoggedOut);

        if (!result) {
            dispatch(
                viewItemSlice.actions.setPlaceBidResponse({
                    isSuccessful: false,
                    validationResult: {
                        code: BID_VALIDATION_RESULTS.OFFLINE
                    }
                })
            );
            dispatch(viewItemSlice.actions.setInitialBidFormValue(amount));
            dispatch(viewItemSlice.actions.setIsBidding(false));
            return;
        }

        const {
            analyticsData,
            bidInfo,
            isSuccessful,
            item: updatedItem,
            validationResult,
            myMaxBidAmount,
            serverSecondsLeft
        } = result;

        if (isSuccessful) {
            dispatch(
                viewItemSlice.actions.setBidInfo({ bidInfo, itemId, memberId })
            );
            dispatch(viewItemSlice.actions.setMyMaxBidAmount(myMaxBidAmount));
            dispatch(
                viewItemSlice.actions.setInitialBidFormValue(
                    bidInfo.nextValidBidAmount
                )
            );
        } else {
            // suggest next valid bid to user
            dispatch(
                viewItemSlice.actions.setInitialBidFormValue(
                    bidInfo.nextValidBidAmount
                )
            );
        }

        analytics.trackBidPlaced(
            itemId,
            validationResult.code,
            analyticsData.isMaxBid,
            analyticsData.potentialBidChange,
            analyticsData.bidId,
            analyticsData.transactionTotal,
            analyticsData.weightedBidValue
        );

        const currentItemDetails = getState().views.viewItem.itemDetails;
        dispatch(
            viewItemSlice.actions.setItemDetails({
                ...currentItemDetails,
                ...updatedItem
            })
        );

        dispatch(
            viewItemSlice.actions.setPlaceBidResponse({
                isSuccessful,
                validationResult,
                bidError: null
            })
        );

        dispatch(viewItemSlice.actions.setServerSecondsLeft(serverSecondsLeft));
    } catch (error) {
        logger(error);
        dispatch(
            viewItemSlice.actions.setPlaceBidResponse({
                isSuccessful: false,
                validationResult: null,
                error
            })
        );
    }
    dispatch(viewItemSlice.actions.setIsBidding(false));
};

export const setNotValidToBidOrBuyNow = () => dispatch => {
    return dispatch(
        viewItemSlice.actions.setPlaceBidResponse({
            isSuccessful: false,
            validationResult: {
                code: BID_VALIDATION_RESULTS.BIDDING_OR_BUYING_OWN_ITEM
            }
        })
    );
};

export const {
    beginLoadingTranslatedDescription,
    failedLoadingTranslatedDescription,
    resetPlaceBidResponse,
    resetTranslatedDescription,
    setBidInfo,
    setIsApprovedBuyer,
    setIsInternationalWacko,
    setViewItemState,
    setTranslatedDescription,
    setTranslationOnDemandDisabled,
    setSellerItems
} = viewItemSlice.actions;
